import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import FormFields from './FormFields';
import { useResolvedForm } from './useResolvedForm';
import { answersToSubmit } from './formConditions';
import PromptModal from './PromptModal';
import { useDialog } from './DialogContext';
import { useAuth } from './AuthContext';
import { canManageForms } from './roles';
import { API_BASE_URL } from './config';
import { getDeviceTimezone } from './dateFormat';
import { isMultiSelect } from './selectValue';
import { getSchemaFields } from './formSections';

// `formId` and `onSubmitted` let a host screen drive this instead of the route:
// a one-time account reaches its survey without a forms list to navigate from,
// and what happens after it submits is the host's business, so the Cancel
// button — which only means "back to the forms list" — steps aside too.
const FormFiller = ({ formId, onSubmitted }) => {
  const { id: routeId } = useParams();
  const id = formId ?? routeId;
  const navigate = useNavigate();
  const { alert } = useDialog();
  const { user } = useAuth();
  const [form, setForm] = useState(null);
  const [schema, setSchema] = useState({ fields: [], calculations: [] });
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [titlePromptOpen, setTitlePromptOpen] = useState(false);

  const resolved = useResolvedForm(schema, formData);
  const { calculatedValues, hiddenIds } = resolved;

  useEffect(() => {
    const fetchForm = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/forms/${id}`);
        setForm(response.data);
        const parsedSchema = JSON.parse(response.data.schema);
        setSchema(parsedSchema);

        const initialData = {};
        getSchemaFields(parsedSchema).forEach((field) => {
          // Multi-answer dropdowns collect a list of options; everything else
          // starts as an empty string.
          initialData[field.id] = isMultiSelect(field) ? [] : '';
        });
        setFormData(initialData);
      } catch (error) {
        console.error('Failed to fetch form:', error);
        await alert({ title: 'Error', message: 'Failed to load form' });
        navigate('/forms');
      } finally {
        setLoading(false);
      }
    };

    fetchForm();
  }, [id, navigate, alert]);

  const handleInputChange = (fieldId, value) => {
    setFormData((prev) => ({
      ...prev,
      [fieldId]: value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setTitlePromptOpen(true);
  };

  const handleTitleConfirm = async (value) => {
    const title = value.trim();
    setTitlePromptOpen(false);
    setSubmitting(true);

    try {
      // Answers to questions that were hidden again after being filled in stay
      // behind, in case the answer that hid them is changed back — but they
      // aren't part of what's submitted.
      const finalData = answersToSubmit(schema, formData, resolved);

      const response = await axios.post(`${API_BASE_URL}/api/submissions`, {
        form_id: id,
        data: finalData,
        title: title || undefined,
        timezone: getDeviceTimezone() || undefined,
      });

      if (onSubmitted) {
        onSubmitted(response.data);
        return;
      }
      await alert({ title: 'Success', message: 'Form submitted successfully!' });
      // Managers land on the form's submission list to see the row they just
      // added; inspectors can't open that screen, so they go back to Forms.
      navigate(canManageForms(user.role) ? `/forms/${id}/submissions` : '/forms');
    } catch (error) {
      console.error('Failed to submit form:', error);
      alert({ title: 'Error', message: 'Failed to submit form. Please try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-800 via-gray-800 to-gray-900 py-8">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center text-white">Loading form...</div>
        </div>
      </div>
    );
  }

  if (!form) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-800 via-gray-800 to-gray-900 py-8">
      <PromptModal
        open={titlePromptOpen}
        title="Submit form"
        message="Give this submission a title to help you find it later. Leave blank to skip."
        defaultValue={form.name}
        placeholder="Submission title"
        confirmLabel="Submit"
        onConfirm={handleTitleConfirm}
        onCancel={() => setTitlePromptOpen(false)}
      />
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <div className="text-center">
            <h1 className="text-3xl font-bold text-white mb-2">{form.name}</h1>
            {form.description && (
              <p className="text-lg text-gray-400">{form.description}</p>
            )}
          </div>
        </div>

        <div className="bg-gray-800/50 backdrop-blur-sm border border-purple-500/20 rounded-xl p-6">
          <form onSubmit={handleSubmit}>
            <FormFields
              schema={schema}
              formData={formData}
              calculatedValues={calculatedValues}
              hiddenIds={hiddenIds}
              onChange={handleInputChange}
            />

            <div className="mt-8 flex justify-end space-x-4">
              {!onSubmitted && (
                <button
                  type="button"
                  onClick={() => navigate('/forms')}
                  className="bg-gray-700/50 hover:bg-gray-600/50 text-gray-300 hover:text-white px-6 py-3 rounded-lg font-medium transition-all duration-200 border border-gray-600 hover:border-gray-500"
                >
                  Cancel
                </button>
              )}
              <button
                type="submit"
                disabled={submitting}
                className="bg-gradient-to-r from-purple-500 to-violet-500 hover:from-purple-600 hover:to-violet-600 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-200 transform hover:scale-105 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? 'Submitting...' : 'Submit Form'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default FormFiller;
